import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Button from '../components/ui/Button';
import ListingCard from '../components/ListingCard';
import Avatar from '../components/ui/Avatar';
import { ArrowRight, Search, MessageCircle, CalendarCheck, Star } from 'lucide-react';

// Placeholder testimonials — swap in real customer reviews once collected.
const REVIEWS = [
  {
    name: 'Fatima Al Suwaidi',
    location: 'Dubai Marina',
    rating: 5,
    quote: "Booked a deep clean before moving into our new apartment and the team finished two hours ahead of schedule. Every cabinet, vent, and windowsill was spotless.",
  },
  {
    name: 'Yousef Al Marzooqi',
    location: 'Abu Dhabi',
    rating: 5,
    quote: "We switched our office cleaning contract to a company we found on Dayra. Same crew every week, and billing is far simpler than our old vendor.",
  },
  {
    name: 'Lara Haddad',
    location: 'Sharjah',
    rating: 4,
    quote: "Move-out cleaning passed our landlord's inspection on the first try. Only reason it's not a perfect score is a slightly late start time.",
  },
  {
    name: 'Omar Bensalem',
    location: 'Dubai',
    rating: 5,
    quote: "Compared three companies through the site in about ten minutes and booked same-day. The price shown up front matched the final invoice exactly.",
  },
  {
    name: 'Noora Al Ali',
    location: 'Ajman',
    rating: 5,
    quote: "Recurring biweekly cleaning for our villa has been reliable for four months straight. Easy to message the team directly if we need to reschedule.",
  },
];

const QUICK_SEARCHES = ['Deep cleaning', 'Move-out cleaning', 'Office cleaning', 'Recurring cleaning'];

const HOW_IT_WORKS = [
  {
    icon: Search,
    title: 'Search what you need',
    desc: 'Filter cleaning listings by service type and price, anywhere in the UAE.',
  },
  {
    icon: MessageCircle,
    title: 'Compare and message',
    desc: "Check a company's listings and reviews, then ask questions before you commit.",
  },
  {
    icon: CalendarCheck,
    title: 'Book and track it',
    desc: 'Confirm a time, follow the order status, and pay once the job is done.',
  },
];

function Stars({ rating, size = 14 }) {
  return (
    <div className="flex gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          size={size}
          className={i <= rating ? 'fill-terra-400 text-terra-400' : 'text-sand-300'}
        />
      ))}
    </div>
  );
}

function ReviewCard({ review }) {
  return (
    <div className="h-full bg-white border border-sand-200 rounded-xl shadow-card p-6 flex flex-col gap-4">
      <Stars rating={review.rating} />
      <p className="text-sm text-ink-700 leading-relaxed flex-1">"{review.quote}"</p>
      <div className="flex items-center gap-3 pt-2 border-t border-sand-100">
        <Avatar name={review.name} size="sm" />
        <div>
          <p className="text-sm font-medium text-ink-900">{review.name}</p>
          <p className="text-xs text-ink-400">{review.location}</p>
        </div>
      </div>
    </div>
  );
}

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

export default function Home() {
  const { user } = useAuth();

  const [featuredListings, setFeaturedListings] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/listings`, { params: { limit: 6 } })
      .then(res => setFeaturedListings(res.data.listings || []))
      .catch(() => setFeaturedListings([]))
      .finally(() => setFeaturedLoading(false));
  }, []);

  const [howRef, howVisible] = useInView();
  const [featRef, featVisible] = useInView();
  const [revRef, revVisible] = useInView();
  const [compRef, compVisible] = useInView();

  return (
    <div className="bg-sand-50 min-h-[calc(100vh-4rem)] overflow-x-hidden">

      {/* ── Hero ─────────────────────────────────────── */}
      <section className="bg-sand-100 border-b border-sand-200 relative overflow-hidden">
        {/* Brand motif — the three overlapping circles, oversized and bleeding off-canvas.
            Clipped at the true section edges (inset-0) rather than an interior boundary,
            so the bleed reads as intentional instead of an accidental crop on narrow screens. */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <svg
            viewBox="0 0 100 100"
            className="absolute top-[2%] right-[-52%] sm:top-1/2 sm:right-[-22%] sm:-translate-y-1/2 w-[110%] sm:w-[120%] max-w-none h-auto animate-heroDrift"
          >
            <circle cx="34" cy="62" r="30" className="fill-sand-300/50" />
            <circle cx="66" cy="62" r="30" className="fill-white/40" />
            <circle cx="50" cy="36" r="30" className="fill-terra-400/[0.14]" />
          </svg>
          <svg viewBox="0 0 100 100" className="hidden sm:block absolute top-[8%] right-[6%] w-24 sm:w-32 h-auto opacity-70">
            <circle cx="34" cy="62" r="30" className="fill-none stroke-terra-400/30" strokeWidth="1.2" />
          </svg>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative py-24 sm:py-32">
          <div className="max-w-xl">
            <p
              className="opacity-0 animate-fadeInUp inline-flex items-center gap-2 text-xs font-semibold text-terra-500 uppercase tracking-widest mb-5"
              style={{ animationFillMode: 'both' }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-terra-400" />
              Cleaning services, UAE-wide
            </p>

            <h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-ink-900 leading-[1.08] mb-6 opacity-0 animate-fadeInUp delay-75"
              style={{ animationFillMode: 'both' }}
            >
              A cleaner space,<br />
              <span className="text-terra-400">a call away.</span>
            </h1>

            <p
              className="text-base text-ink-400 leading-relaxed mb-8 max-w-md opacity-0 animate-fadeInUp delay-150"
              style={{ animationFillMode: 'both' }}
            >
              Search, compare, and book trusted cleaning companies across the UAE — with prices you see before you book.
            </p>

            {/* Primary CTA */}
            <div
              className="opacity-0 animate-fadeInUp delay-225 mb-5"
              style={{ animationFillMode: 'both' }}
            >
              <Link to="/listings">
                <Button variant="primary" size="lg">
                  Browse cleaning services
                  <ArrowRight size={16} />
                </Button>
              </Link>
            </div>

            {/* Quick searches */}
            <div
              className="flex flex-wrap gap-2 mb-8 opacity-0 animate-fadeInUp delay-300"
              style={{ animationFillMode: 'both' }}
            >
              {QUICK_SEARCHES.map(term => (
                <Link
                  key={term}
                  to={`/listings?search=${encodeURIComponent(term)}`}
                  className="px-3.5 py-1.5 rounded-full bg-white/70 border border-sand-200 text-xs font-medium text-ink-700 hover:border-terra-400/50 hover:text-terra-500 hover:bg-white transition-colors duration-200"
                >
                  {term}
                </Link>
              ))}
            </div>

            {/* Trust row */}
            <div
              className="flex items-center gap-2.5 opacity-0 animate-fadeInUp delay-400"
              style={{ animationFillMode: 'both' }}
            >
              <Stars rating={5} size={15} />
              <span className="text-sm text-ink-400">4.9 average from verified bookings</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Featured Listings ────────────────────────── */}
      {(featuredLoading || featuredListings.length > 0) && (
        <section ref={featRef} className="bg-white py-14 sm:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div
              className="flex items-end justify-between mb-8"
              style={{
                opacity: featVisible ? 1 : 0,
                transform: featVisible ? 'translateY(0)' : 'translateY(12px)',
                transition: 'opacity 600ms ease, transform 600ms ease',
              }}
            >
              <div>
                <p className="text-xs font-semibold text-terra-400 uppercase tracking-widest mb-1">Trusted by locals</p>
                <h2 className="font-heading text-2xl font-semibold text-ink-900">Popular services</h2>
              </div>
              <Link
                to="/listings"
                className="flex items-center gap-1 text-sm text-terra-400 hover:text-terra-500 transition-colors"
              >
                Browse all <ArrowRight size={14} />
              </Link>
            </div>

            {featuredLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {[1, 2, 3].map(n => (
                  <div key={n} className="bg-sand-100 animate-pulse rounded-xl h-72" />
                ))}
              </div>
            ) : (
              <>
                {/* Mobile: horizontal scroll */}
                <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 [&::-webkit-scrollbar]:hidden sm:hidden">
                  {featuredListings.map(listing => (
                    <div key={listing.id} className="snap-start flex-shrink-0 w-[280px]">
                      <ListingCard listing={listing} />
                    </div>
                  ))}
                </div>
                {/* Desktop: staggered grid */}
                <div className="hidden sm:grid sm:grid-cols-3 gap-6">
                  {featuredListings.map((listing, i) => (
                    <div
                      key={listing.id}
                      style={{
                        opacity: featVisible ? 1 : 0,
                        transform: featVisible ? 'translateY(0)' : 'translateY(16px)',
                        transition: `opacity 500ms ease ${100 + i * 80}ms, transform 500ms ease ${100 + i * 80}ms`,
                      }}
                    >
                      <ListingCard listing={listing} />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* ── How it works ─────────────────────────────── */}
      <section ref={howRef} className="bg-white border-t border-b border-sand-200 py-14 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-6 sm:divide-x sm:divide-sand-200">
            {HOW_IT_WORKS.map((step, i) => (
              <div
                key={step.title}
                className="flex sm:flex-col gap-4 sm:gap-3 sm:px-6 sm:first:pl-0 sm:last:pr-0 transition-all duration-600"
                style={{
                  opacity: howVisible ? 1 : 0,
                  transform: howVisible ? 'translateY(0)' : 'translateY(10px)',
                  transitionDelay: `${i * 100}ms`,
                  transitionDuration: '600ms',
                }}
              >
                <div className="w-10 h-10 rounded-full bg-terra-400/10 text-terra-500 flex items-center justify-center shrink-0">
                  <step.icon size={18} />
                </div>
                <div>
                  <p className="font-heading text-base font-semibold text-ink-900 mb-1">
                    {i + 1}. {step.title}
                  </p>
                  <p className="text-sm text-ink-400 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Customer Reviews ─────────────────────────── */}
      <section ref={revRef} className="bg-sand-100 border-t border-sand-200 py-14 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="mb-10"
            style={{
              opacity: revVisible ? 1 : 0,
              transform: revVisible ? 'translateY(0)' : 'translateY(12px)',
              transition: 'opacity 600ms ease, transform 600ms ease',
            }}
          >
            <p className="text-xs font-semibold text-terra-400 uppercase tracking-widest mb-1">Customer stories</p>
            <div className="flex items-end justify-between flex-wrap gap-2">
              <h2 className="font-heading text-2xl font-semibold text-ink-900">What customers are saying</h2>
              <div className="flex items-center gap-2">
                <Stars rating={5} size={16} />
                <span className="text-sm text-ink-400">4.9 average from verified bookings</span>
              </div>
            </div>
          </div>

          {/* Mobile: horizontal scroll */}
          <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 [&::-webkit-scrollbar]:hidden sm:hidden">
            {REVIEWS.map(review => (
              <div key={review.name} className="snap-start flex-shrink-0 w-[280px]">
                <ReviewCard review={review} />
              </div>
            ))}
          </div>

          {/* Desktop: staggered grid */}
          <div className="hidden sm:grid sm:grid-cols-3 gap-6">
            {REVIEWS.map((review, i) => (
              <div
                key={review.name}
                style={{
                  opacity: revVisible ? 1 : 0,
                  transform: revVisible ? 'translateY(0)' : 'translateY(16px)',
                  transition: `opacity 500ms ease ${100 + i * 80}ms, transform 500ms ease ${100 + i * 80}ms`,
                }}
              >
                <ReviewCard review={review} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Company CTA ──────────────────────────────── */}
      {!user && (
        <section ref={compRef} className="bg-ink-900 py-16 sm:py-20 relative overflow-hidden">
          {/* Brand circle motif */}
          <div className="absolute -right-16 top-1/2 -translate-y-1/2 w-[420px] h-[420px] pointer-events-none opacity-[0.08]">
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <circle cx="34" cy="62" r="30" fill="#D4C9B0" />
              <circle cx="66" cy="62" r="30" fill="#E8DECA" />
              <circle cx="50" cy="36" r="30" fill="#2D6A4F" />
            </svg>
          </div>

          <div
            className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative"
            style={{
              opacity: compVisible ? 1 : 0,
              transform: compVisible ? 'translateY(0)' : 'translateY(16px)',
              transition: 'opacity 700ms ease, transform 700ms ease',
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">

              {/* Left — copy */}
              <div>
                <p className="text-xs font-semibold text-terra-400 uppercase tracking-widest mb-4">
                  For cleaning companies
                </p>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white leading-tight mb-4">
                  Your next client is<br />already looking.
                </h2>
                <p className="text-sm text-sand-300 leading-relaxed max-w-sm">
                  List your services on Dayra and get discovered by customers across the UAE — no cold outreach, no hidden fees.
                </p>
              </div>

              {/* Right — CTA */}
              <div
                className="flex flex-col gap-4 md:items-end"
                style={{
                  opacity: compVisible ? 1 : 0,
                  transform: compVisible ? 'translateY(0)' : 'translateY(12px)',
                  transition: 'opacity 700ms ease 150ms, transform 700ms ease 150ms',
                }}
              >
                <div className="flex gap-3 flex-wrap md:justify-end">
                  <Link to="/signup/company">
                    <Button variant="primary" size="lg">Join as a Company</Button>
                  </Link>
                </div>
              </div>

            </div>
          </div>
        </section>
      )}

    </div>
  );
}
